<script setup lang="ts">
import {onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch} from "vue"
import {usePlatformStore} from "@/stores/platform.store"
import Loader from "@/components/Loader.vue"
import Modal from "@/components/Modals/Modal.vue"
import {findAndReplace} from "@/helpers/text.helper"
import modalRepository, {ModalContent} from "@/repositories/modal.repository"
import {useFocusWithin} from "@vueuse/core"

const props = defineProps<{
    activityId: number,
    platformUrl: string,
}>()

const emits = defineEmits<{
    (e: "cancel"): void
}>()

const platformStore = usePlatformStore();

const state = reactive<{
    isLoading: boolean,
    modalContent?: ModalContent,
    redirectTimer?: NodeJS.Timeout
}>({
    isLoading: true,
    modalContent: undefined,
    redirectTimer: undefined,
})

const modalRef = ref()
const {focused} = useFocusWithin(modalRef)

const redirectUrl = `${props.platformUrl}/activiteit-detail/reserveren?id=${props.activityId}`;

// Cancel the redirect and cleanup the event listener
const cancelRedirect = () => {
    if (state.redirectTimer) {
        clearTimeout(state.redirectTimer)
        state.redirectTimer = undefined
    }
    window.removeEventListener("keydown", handleCancelKeyPress)
    emits("cancel")
}

// Handle the keyboard events that should cancel the redirect
const handleCancelKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
        cancelRedirect()
    }
}

watch(focused, (newValue, oldValue) => {
    if (oldValue && !newValue) cancelRedirect()
})

onBeforeMount(async () => {
    await platformStore.ensurePlatform();
    const currentPlatformId = platformStore.platformId
    const currentPlatformUrl = platformStore.platform?.Url ?? ""

    state.modalContent = await modalRepository.get("redirectModal", currentPlatformId, "", redirectUrl, currentPlatformUrl)
        .then((data) => {
            const placeHolders = [
                {find: "{{van_link}}", replace: "<b>" + platformStore.platform?.Naam + "</b>"},
                {find: "{{naar_link}}", replace: "<b>" + new URL(redirectUrl).hostname + "</b>"},
                {find: "{{van_platform}}", replace: "<b>" + data.RedirectFromPlatform + "</b>"},
                {find: "{{naar_platform}}", replace: "<b>" + data.RedirectToPlatform + "</b>"},
            ]
            placeHolders.forEach((placeHolder) => data.Text = findAndReplace(data.Text, placeHolder.find, placeHolder.replace))

            return data;
        }).catch(() => {
            return {
                Title: "Redirect",
                ClosingButtonText: "Annuleren",
                Text: "U wordt doorgestuurd naar een andere website.",
                RedirectFromLogo: "",
                RedirectToLogo: "",
                RedirectFromPlatform: "",
                RedirectToPlatform: "",
            }
        });

    state.redirectTimer = setTimeout(() => {
        window.location.href = redirectUrl
    }, 5000);

    state.isLoading = false
})

onMounted(() => {
    window.addEventListener("keydown", handleCancelKeyPress);
    modalRef.value?.focus();
})

onBeforeUnmount(() => {
    cancelRedirect();
})
</script>

<template>
    <Modal :show="true">
        <div ref="modalRef" class="sport-in-modal redirect-modal" tabindex="-1">
            <div v-if="state.isLoading" class="d-flex justify-content-center align-items-center h-50">
                <Loader :is-active="state.isLoading" :full-page="false"/>
            </div>

            <div v-if="!state.isLoading" class="sport-in-modal-header redirect-modal-header">
                <p class="sport-in-modal-heading">{{ state.modalContent?.Title }}</p>
            </div>
            <div v-if="!state.isLoading" class="sport-in-modal-body redirect-modal-body">
                <div class="row justify-content-center mb-4" aria-label="Redirect naar nieuwe platform">

                    <img class="col-lg-3" 
                         :src="state.modalContent?.RedirectFromLogo"
                         alt=""/>
                    <span
                        class="d-lg-flex d-none h3 mb-0 justify-content-center align-items-center col-lg-2 icon icon-arrow-right" aria-hidden="true"></span>
                    <span
                        class="d-flex d-lg-none h1 my-2 justify-content-center align-items-center col-lg-2 icon icon-arrow-down" aria-hidden="true"></span>
                    <img class="col-lg-3" :src="state.modalContent?.RedirectToLogo"
                         alt=""/>
                </div>

                <p class="text-center" v-html="state.modalContent?.Text"/>
            </div>
        </div>
    </Modal>
</template>
